<template>
  <lf-checkbox-dropdown
    ref="checkboxDropdown"
    :get-paginated-options="getUsersByRole"
    :options="users"
    :placeholder="$t(placeholderKey, assigneeIdsValue?.length || 1)"
    api-query-param="search"
    class="activity-hub-filter-dropdown z-21 h-6-5 min-w-full leading-none"
    dropdown-width-full
    custom-dropdown-class="z-30"
    enable-search
    name="assignee_ids"
    width-full
    no-margin
    sort
    :toggle-from-parent
    :sorting-function="sortUsers"
    :model-value="selected || []"
    :render-component="UserOption"
    :render-component-options="renderComponentOptions"
    @menu:closed="selected = getArrayQueryValue(queryKey)"
    @update:model-value="handleModelValueUpdate"
    @loaded-more="updateAssigneeOptions($event)"
    @options-search="updateAssigneeOptions($event, true)"
  >
    <template #pre-search>
      <div class="flex items-center justify-between px-4 py-2">
        <lf-h4><slot></slot></lf-h4>
        <button class="text-error" @click="handleModelValueUpdate([])">
          {{ $t("COMMON.CLEAR") }}
        </button>
      </div>
    </template>
    <template #footer>
      <div class="border-t p-4">
        <button
          class="apply-filters-button w-full rounded-sm py-[10px] text-center font-semibold text-primary"
          @click.stop="handleApplyFilter"
        >
          {{ $t("COMMON.APPLY") }}
        </button>
      </div>
    </template>
  </lf-checkbox-dropdown>
</template>
<script lang="ts">
import { useRouteQuery } from "@vueuse/router";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";

import { convertToArray } from "@/helpers/common";
import { useAuth } from "@/hooks/auth";
import { usePromiseWrapper } from "@/hooks/common";
import { useCommunicationLogs } from "@/hooks/communicationLogs";
import emailService from "@/services/modules/emails";
import usersApiService from "@/services/modules/users";

import type { IUser } from "@/models/users";

type UserType = "user" | "email_recipient";
</script>

<script setup lang="ts">
import UserOption from "@/components/communicationLogs/filters/UserOption.vue";
import LfCheckboxDropdown from "@/components/ui/inputs/LfCheckboxDropdown.vue";

const {
  queryKey = "assignee_ids",
  type = "user",
  placeholderKey = "ACTIVITY.FILTERS.ASSIGNEE",
  toggleFromParent = false,
} = defineProps<{
  queryKey?: string;
  placeholderKey?: string;
  type?: UserType;
  toggleFromParent?: boolean;
}>();

const auth = useAuth();
const { handleDropdownValueUpdate, handleFiltering, getArrayQueryValue } =
  useCommunicationLogs();
const route = useRoute();

const assigneeIdsValue = useRouteQuery(queryKey);

const users = ref<Record<string, string>>({});
const selected = ref<string[]>(useRouteQuery(queryKey, []).value);
const checkboxDropdown = ref<InstanceType<typeof LfCheckboxDropdown> | null>(
  null
);

const checkForValidFilterOptionsBasedOnQuery = async (
  assigneeIds: string[] | undefined
) => {
  const missingAssignees = assigneeIds?.filter(
    (id) => !Object.keys(users.value).includes(id)
  );
  if (!missingAssignees?.length) {
    return;
  }
  const addedUsers = await getUsersById(missingAssignees);
  if (addedUsers?.length) {
    updateAssigneeOptions(addedUsers);
  }
};

const sortUsers = (a: string, b: string) => {
  return a.localeCompare(b);
};

const assigneeIdsArray = computed(() => {
  const assigneeIds = convertToArray(assigneeIdsValue.value);
  return assigneeIds.length ? assigneeIds : undefined;
});

const renderComponentOptions = computed(() => ({
  selected: assigneeIdsArray.value,
}));

const { fetchWrapper: getClientUsers } = usePromiseWrapper(
  async (params: Record<string, string | number> = {}, id: string) =>
    usersApiService.getClientUsers(id, {
      ...params,
      with_preset: 0,
    })
);

const { fetchWrapper: getEmployees } = usePromiseWrapper(
  async (params: Record<string, string | number> = {}) =>
    usersApiService.getEmployees({ ...params, with_preset: 0 })
);

const { fetchWrapper: getUsersById } = usePromiseWrapper(
  async (ids: string[] | number[]) => {
    if (!ids.length) {
      return [];
    }
    const params = new URLSearchParams(
      ids.map((id) => ["ids[]", id as string])
    );
    return await usersApiService.getUsers(params);
  }
);

const { fetchWrapper: getClientEmailRecipients } = usePromiseWrapper(
  async (params: Record<string, string | number> = {}, id: string) =>
    usersApiService.getClientEmailRecipients(id, params)
);

const { fetchWrapper: getLendflowEmailRecipients } = usePromiseWrapper(
  async (params: Record<string, string | number> = {}) =>
    emailService.getEmailRecipients(params)
);

const userRequestMap = {
  client: {
    user: getClientUsers,
    email_recipient: getClientEmailRecipients,
  },
  lendflow: {
    user: getEmployees,
    email_recipient: getLendflowEmailRecipients,
  },
};

const getUsersByRole = (params: Record<string, string | number> = {}) => {
  if (auth.isClient && auth.currentUser?.client_id) {
    return userRequestMap.client[type](
      params,
      auth.currentUser.client_id
    );
  }
  return userRequestMap.lendflow[type](params);
};

const updateAssigneeOptions = (data: unknown[], search = false) => {
  const newPartnersOptions = (data as IUser[]).reduce<Record<string, string>>(
    (acc, { id, first_name, last_name }) => {
      if (id && first_name && last_name) {
        acc[id] = `${first_name} ${last_name}`;
      }
      return acc;
    },
    {}
  );

  if (search) {
    users.value = newPartnersOptions;
    return;
  }
  users.value = Object.assign({}, users.value, newPartnersOptions);
  checkForValidFilterOptionsBasedOnQuery(assigneeIdsArray.value);
};

const handleApplyFilter = async () => {
  await handleFiltering({
    value: selected.value,
    queryKey: queryKey,
    isMultiple: true,
  });
  if (checkboxDropdown.value) {
    checkboxDropdown.value.showMenu = false;
  }
};

const handleModelValueUpdate = async (value: unknown[]) => {
  const result = await handleDropdownValueUpdate({
    value,
    queryKey: queryKey,
    isMultiple: true,
    selected: selected.value,
  });
  selected.value = result;
};

watch(
  () => route.query[queryKey],
  (updatedValue) => {
    if (!updatedValue) {
      selected.value = [];
      return;
    }
    selected.value = convertToArray(updatedValue) as string[];
  },
  { immediate: true }
);
</script>

<style scoped>
@reference "@/assets/tailwind.css";

svg {
  margin-bottom: 0;
}
</style>
<style scoped>
@reference "@/assets/tailwind.css";

.activity-hub-filter-dropdown :deep() {
  .dropdown-wrapper {
    @apply min-w-100;
  }
}
</style>
